<template>
  <iColumn
    gap="small"
    width="fill"
    data-test="story-comment"
    :data-comment-id="comment.id"
  >
    <iRow
      gap="standard"
      width="fill"
      vertical-align="top"
      wrap="nowrap"
      data-test="story-comment-main"
    >
      <iAvatar :name="comment.authorName" :src="comment.avatarUrl" size="standard" />
      <iColumn gap="extraSmall" width="fill" :min-width="0">
        <iRow gap="small" vertical-align="middle">
          <iText
            font-weight="dark"
            font-size="small"
            :style-overrides="fontStyle"
            data-test="story-comment-author"
          >
            {{ comment.authorName || "Someone" }}
          </iText>
          <iText variant="subtle" font-size="small">
            {{ postedAgo }}
          </iText>
          <iText
            v-if="comment.edited"
            variant="subtle"
            font-size="small"
            data-test="story-comment-edited"
          >
            edited
          </iText>
          <iText
            v-if="comment.pending"
            variant="subtle"
            font-size="small"
            data-test="story-comment-pending"
          >
            {{ pendingLabel }}
          </iText>
        </iRow>
        <iCommentComposer
          v-if="editing"
          :on-submit="saveEdit"
          :initial-segments="comment.segments"
          :completions="completions"
          :input-style-overrides="COMMENT_INPUT_STYLE"
          :send-variant="COMMENT_SEND_VARIANT"
          :send-style-overrides="COMMENT_SEND_STYLE"
          submit-label="Save"
          show-cancel
          autofocus
          @cancel="editing = false"
        />
        <iColumn v-else width="fill" data-test="story-comment-body">
          <iMessageSegmentsPreview
            v-for="(paragraph, index) in comment.segments"
            :key="index"
            :segments="[paragraph]"
          />
        </iColumn>
        <iRow
          v-if="!editing"
          gap="standard"
          vertical-align="middle"
          data-test="story-comment-actions"
        >
          <template v-if="isHeldForModeration">
            <iButton
              variant="text"
              size="small"
              :is-loading="moderating === MODERATION_APPROVE"
              :style-overrides="META_LINK"
              data-test="story-comment-approve"
              @click="moderate(MODERATION_APPROVE)"
            >
              Approve
            </iButton>
            <iButton
              variant="text"
              size="small"
              :is-loading="moderating === MODERATION_BLOCK"
              :style-overrides="META_LINK"
              data-test="story-comment-block-comment"
              @click="moderate(MODERATION_BLOCK)"
            >
              Block
            </iButton>
            <iButton
              v-if="!comment.escalated"
              variant="text"
              size="small"
              :is-loading="moderating === MODERATION_ESCALATE"
              :style-overrides="META_LINK"
              data-test="story-comment-escalate"
              @click="moderate(MODERATION_ESCALATE)"
            >
              Escalate
            </iButton>
          </template>
          <template v-if="canVoteOnComments && !isHeldForModeration">
            <iButton
              variant="text"
              size="small"
              :icon-left="comment.vote === VOTE_UP ? ICONS.upvoted : ICONS.upvote"
              :aria-pressed="comment.vote === VOTE_UP"
              aria-label="Upvote"
              data-test="story-comment-upvote"
              @click="storyComments.vote(storyId, comment, VOTE_UP)"
            />
            <iText font-size="small" data-test="story-comment-score">
              {{ comment.score }}
            </iText>
            <iButton
              variant="text"
              size="small"
              :icon-left="comment.vote === VOTE_DOWN ? ICONS.downvoted : ICONS.downvote"
              :aria-pressed="comment.vote === VOTE_DOWN"
              aria-label="Downvote"
              data-test="story-comment-downvote"
              @click="storyComments.vote(storyId, comment, VOTE_DOWN)"
            />
          </template>
          <iButton
            v-if="canReply"
            variant="text"
            size="small"
            :style-overrides="META_LINK"
            data-test="story-comment-reply"
            @click="$emit('reply', comment)"
          >
            Reply
          </iButton>
          <iButton
            v-if="isOwnComment"
            variant="text"
            size="small"
            :style-overrides="META_LINK"
            data-test="story-comment-edit"
            @click="editing = true"
          >
            Edit
          </iButton>
          <iButton
            v-if="isOwnComment"
            variant="text"
            size="small"
            :style-overrides="META_LINK"
            data-test="story-comment-delete"
            @click="confirmDelete"
          >
            Delete
          </iButton>
          <iButton
            v-if="!isOwnComment && !comment.reported && !isHeldForModeration"
            variant="text"
            size="small"
            :is-loading="reporting"
            :style-overrides="META_LINK"
            data-test="story-comment-report"
            @click="openReportReasons"
          >
            Report
          </iButton>
          <iText
            v-if="comment.reported"
            variant="subtle"
            font-size="small"
            data-test="story-comment-reported"
          >
            Reported
          </iText>
          <iButton
            v-if="canBlockAuthor"
            variant="text"
            size="small"
            :style-overrides="META_LINK"
            data-test="story-comment-block"
            @click="confirmBlock"
          >
            Block
          </iButton>
        </iRow>
        <iText v-if="editError" variant="error" font-size="small">
          {{ editError }}
        </iText>
        <iText
          v-if="moderationError"
          variant="error"
          font-size="small"
          data-test="story-comment-moderation-error"
        >
          {{ moderationError }}
        </iText>
        <iText
          v-if="reportError"
          variant="error"
          font-size="small"
          data-test="story-comment-report-error"
        >
          {{ reportError }}
        </iText>
      </iColumn>
      <iButton
        v-if="canLikeComments && !editing && !isHeldForModeration"
        variant="text"
        size="small"
        :icon-top="comment.liked ? ICONS.liked : ICONS.like"
        :icon-top-variant="comment.liked ? 'error' : 'subtle'"
        :icon-color="comment.liked ? likedHeartColor : 'inherit'"
        icon-top-size="small"
        :style-overrides="HEART"
        :aria-pressed="String(comment.liked)"
        :aria-label="likeLabel"
        data-test="story-comment-like"
        @click="storyComments.toggleLike(storyId, comment)"
      >
        {{ comment.likeCount > 0 ? comment.likeCount : "" }}
      </iButton>
    </iRow>
    <slot />
    <iModal
      v-if="confirmingDelete"
      title="Delete this comment?"
      primary-action-button-label="Delete"
      secondary-action-button-label="Cancel"
      :max-width="DELETE_DIALOG_MAX_WIDTH"
      :close-on-backdrop="backdropSettled"
      data-test="story-comment-delete-confirm"
      @click:primary="deleteNow"
      @close="confirmingDelete = false"
    >
      <template #body>
        <iText variant="subtle">
          It will be removed for everyone. This can't be undone.
        </iText>
      </template>
    </iModal>
    <iModal
      v-if="confirmingBlock"
      :title="`Block ${authorName}?`"
      primary-action-button-label="Block"
      secondary-action-button-label="Cancel"
      :max-width="BLOCK_DIALOG_MAX_WIDTH"
      :close-on-backdrop="backdropSettled"
      data-test="story-comment-block-confirm"
      @click:primary="blockNow"
      @close="confirmingBlock = false"
    >
      <template #body>
        <iText variant="subtle">
          You won't see their comments or replies here or in this destination's chat. They won't be told.
        </iText>
      </template>
    </iModal>
    <iModal
      v-if="pickingReportReason"
      title="Report this comment"
      :show-footer="false"
      :max-width="REPORT_DIALOG_MAX_WIDTH"
      :close-on-backdrop="backdropSettled"
      data-test="story-comment-report-reasons"
      @close="pickingReportReason = false"
    >
      <template #body>
        <iColumn gap="small" width="fill" wrap="nowrap">
          <iText variant="subtle">
            Tell the moderators what is wrong with it.
          </iText>
          <iButton
            v-for="reason in REPORT_REASONS"
            :key="reason.value"
            variant="secondary"
            width="fill"
            text-alignment="left"
            @click="sendReport(reason.value)"
          >
            {{ reason.label }}
          </iButton>
        </iColumn>
      </template>
    </iModal>
  </iColumn>
</template>

<script>
import { mapState } from "pinia";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import { VOTE_DOWN, VOTE_UP, useStoryCommentsStore } from "%/dynamic-component-editor/stores/story-comments-store";
import { timeAgo } from "%/dynamic-component-editor/helpers/story-comments.mjs";
import {
  MODERATION_APPROVE,
  MODERATION_BLOCK,
  MODERATION_ESCALATE,
} from "%/dynamic-component-editor/constants/story-engagement-constants.mjs";
import { ENGAGEMENT_ICONS } from "%/dynamic-component-editor/standard-elements/action-element/engagement-action-mixin";
import { REPORT_REASONS } from "%/helpers/report-reasons.mjs";
import { BackdropSettleMixin } from "%/dynamic-component-editor/mixins/backdrop-settle-mixin";
import {
  COMMENT_INPUT_STYLE,
  COMMENT_SEND_STYLE,
  COMMENT_SEND_VARIANT,
} from "%/dynamic-component-editor/constants/comment-composer-style";

const DELETE_DIALOG_MAX_WIDTH = 420;
const NO_SMALL_PADDING = Object.freeze({
  smallPaddingTop: "none",
  smallPaddingRight: "none",
  smallPaddingBottom: "none",
  smallPaddingLeft: "none",
});
const META_LINK = Object.freeze({
  ...NO_SMALL_PADDING,
  textVariantFontColor: "subtle",
  textVariantHoverFontColor: "standard",
  textVariantFontWeight: "dark",
});
const HEART = Object.freeze({
  ...NO_SMALL_PADDING,
  textVariantFontColor: "subtle",
  textVariantHoverFontColor: "subtle",
});
const REPORT_DIALOG_MAX_WIDTH = 420;
const BLOCK_DIALOG_MAX_WIDTH = 420;

export default {
  name: "StoryCommentItem",
  mixins: [BackdropSettleMixin],
  props: {
    storyId: {
      type: Number,
      required: true,
    },
    comment: {
      type: Object,
      required: true,
    },
    completions: {
      type: Array,
      default: () => [],
    },
    fontStyle: {
      type: Object,
      default: () => ({}),
    },
    activeIconColor: {
      type: String,
      default: "",
    },
  },
  emits: ["reply"],
  data() {
    return {
      editing: false,
      editError: "",
      confirmingDelete: false,
      confirmingBlock: false,
      pickingReportReason: false,
      reporting: false,
      reportError: "",
      moderating: "",
      moderationError: "",
      MODERATION_APPROVE,
      MODERATION_BLOCK,
      MODERATION_ESCALATE,
      DELETE_DIALOG_MAX_WIDTH,
      REPORT_DIALOG_MAX_WIDTH,
      BLOCK_DIALOG_MAX_WIDTH,
      VOTE_UP,
      VOTE_DOWN,
      REPORT_REASONS,
      ICONS: ENGAGEMENT_ICONS,
      COMMENT_INPUT_STYLE,
      COMMENT_SEND_STYLE,
      COMMENT_SEND_VARIANT,
      META_LINK,
      HEART,
    };
  },
  computed: {
    ...mapState(useConfigurationStore, ["canLikeComments", "canVoteOnComments", "canReplyToComments", "canCommentOnPosts"]),
    ...mapState(useStoryStore, ["userId"]),
    storyComments() {
      return useStoryCommentsStore();
    },
    isOwnComment() {
      return this.userId > 0 && this.comment.authorId === this.userId;
    },
    authorName() {
      return this.comment.authorName || "this person";
    },
    isHeldForModeration() {
      return this.comment.pending && !this.isOwnComment && this.storyComments.thread(this.storyId).canModerate;
    },
    pendingLabel() {
      return this.isHeldForModeration && this.comment.escalated ? "Escalated" : "Waiting for approval";
    },
    canBlockAuthor() {
      return this.userId > 0 && !this.isOwnComment && this.comment.authorId > 0 && this.storyComments.thread(this.storyId).native
        && !this.isHeldForModeration;
    },
    canReply() {
      return this.canCommentOnPosts && this.canReplyToComments && !this.comment.pending;
    },
    likedHeartColor() {
      return this.activeIconColor
        || this.storyComments.likedHeartColor(this.storyId)
        || this.$getStyleValuePropertiesFromTheme("color.font").error;
    },
    likeLabel() {
      return this.comment.liked ? "Unlike" : "Like";
    },
    postedAgo() {
      return timeAgo(this.comment.createdAt, Math.floor(Date.now() / 1000));
    },
  },
  methods: {
    openReportReasons() {
      if (this.storyComments.requireSignedInReader()) {
        this.reportError = "";
        this.settleBackdrop();
        this.pickingReportReason = true;
      }
    },
    confirmDelete() {
      this.settleBackdrop();
      this.confirmingDelete = true;
    },
    confirmBlock() {
      this.settleBackdrop();
      this.confirmingBlock = true;
    },
    blockNow() {
      this.confirmingBlock = false;
      this.storyComments.blockAuthor(this.storyId, this.comment);
    },
    sendReport(reason) {
      this.pickingReportReason = false;
      this.reporting = true;
      this.reportError = "";
      this.storyComments.reportComment(this.storyId, this.comment, reason).catch(error => {
        this.reportError = error.message;
      }).finally(() => {
        this.reporting = false;
      });
    },
    moderate(action) {
      if (this.moderating) {
        return;
      }
      this.moderating = action;
      this.moderationError = "";
      this.storyComments.moderateComment(this.storyId, this.comment, action).catch(error => {
        this.moderationError = error.message;
      }).finally(() => {
        this.moderating = "";
      });
    },
    deleteNow() {
      this.confirmingDelete = false;
      this.storyComments.deleteComment(this.storyId, this.comment);
    },
    saveEdit(segments) {
      this.editError = "";
      return this.storyComments.editComment(this.storyId, this.comment, segments).then(() => {
        this.editing = false;
      }, error => {
        this.editError = error.message;
        throw error;
      });
    },
  },
};
</script>
